import { useEffect } from 'react'
import { useTheme } from '@/shared/hooks/useTheme'
import type { BrandShellAppearance } from '@/shared/schemas/brand.schema'
import { getActiveThemeSlug } from '@/themes/registry'

function hexColor(record: Record<string, unknown> | undefined, key: string): string | undefined {
  const raw = record?.[key]
  if (typeof raw !== 'string') {
    return undefined
  }
  return /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(raw) ? raw : undefined
}

const RADIUS_PRESETS: Record<string, { xs: string; sm: string; md: string; lg: string; xl: string }> = {
  sharp: { xs: '2px', sm: '4px', md: '8px', lg: '12px', xl: '16px' },
  default: { xs: '4px', sm: '8px', md: '12px', lg: '18px', xl: '24px' },
  round: { xs: '6px', sm: '12px', md: '18px', lg: '26px', xl: '34px' },
}

const FONT_FAMILIES: Record<string, string> = {
  nunito: "'Nunito', 'Montserrat', -apple-system, system-ui, sans-serif",
  montserrat: "'Montserrat', 'Nunito', -apple-system, system-ui, sans-serif",
  instrument_serif: "'Instrument Serif', 'EB Garamond', Georgia, serif",
  geist: "'Geist', Inter, system-ui, -apple-system, sans-serif",
  inter: "'Inter', 'Geist', system-ui, -apple-system, sans-serif",
}

function typographyValue(record: Record<string, unknown> | undefined, key: string): string | undefined {
  const raw = record?.[key]
  return typeof raw === 'string' ? FONT_FAMILIES[raw] : undefined
}

/** Light-only surfaces/ink from brand DB must not override dark theme tokens. */
const LIGHT_SURFACE_KEYS = [
  '--brand-background',
  '--brand-surface',
  '--brand-surface-strong',
  '--brand-surface-tint',
  '--brand-border',
  '--brand-border-strong',
  '--brand-border-soft',
  '--brand-text-primary',
  '--brand-text-secondary',
  '--brand-text-muted',
  '--brand-muted',
  '--brand-ring',
  '--brand-scrim',
] as const

/** Pale tints and dark-on-light "strong" shades: wrong on dark surfaces, dark CSS tokens supply them. */
const LIGHT_TINT_KEYS = [
  '--brand-primary-strong',
  '--brand-primary-soft',
  '--brand-secondary-soft',
  '--brand-accent-soft',
  '--brand-error-soft',
  '--brand-success-soft',
  '--brand-warning-soft',
  '--brand-info-soft',
] as const

/** Light-mode brand solids: inline values stay dark teal and fail AA on dark surfaces (S9-06). */
const LIGHT_SOLID_BRAND_KEYS = [
  '--brand-primary',
  '--brand-primary-hover',
  '--brand-primary-active',
  '--brand-primary-disabled',
  '--brand-on-primary',
  '--brand-secondary',
  '--brand-secondary-hover',
  '--brand-accent',
] as const

/** Applique les couleurs de marque depuis l’API sur :root (--brand-*), consommées par @theme Tailwind. */
export function BrandCssVariables(props: { brand: BrandShellAppearance | undefined }) {
  const colors = props.brand?.colors
  const typography = props.brand?.typography
  const radiusPreset = props.brand?.radius_preset
  const customTokens = props.brand?.custom_tokens
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === 'dark'

  // NB : `<meta name="theme-color">` est géré exclusivement par AppearanceProvider
  // (valeurs dark/light déterministes). Ne pas l'écrire ici pour éviter le conflit.

  useEffect(() => {
    const root = document.documentElement
    const activeTheme = getActiveThemeSlug()
    root.dataset.activeTheme = activeTheme
    root.dataset.brandTheme = props.brand?.theme_slug ?? activeTheme
    const set = (name: string, value: string | undefined) => {
      if (value === undefined || value === '') {
        root.style.removeProperty(name)
        return
      }
      root.style.setProperty(name, value)
    }

    const brandKeys = [
      '--brand-primary',
      '--brand-primary-hover',
      '--brand-primary-active',
      '--brand-primary-disabled',
      '--brand-primary-strong',
      '--brand-primary-soft',
      '--brand-on-primary',
      '--brand-secondary',
      '--brand-secondary-hover',
      '--brand-secondary-soft',
      '--brand-accent',
      '--brand-accent-soft',
      ...LIGHT_SURFACE_KEYS,
      '--brand-error',
      '--brand-error-soft',
      '--brand-success',
      '--brand-success-soft',
      '--brand-warning',
      '--brand-warning-soft',
      '--brand-info',
      '--brand-info-soft',
      '--brand-radius-xs',
      '--brand-radius-sm',
      '--brand-radius-md',
      '--brand-radius-lg',
      '--brand-radius-xl',
      '--brand-font-heading',
      '--brand-font-body',
    ] as const

    if (isDark) {
      for (const key of [...LIGHT_SURFACE_KEYS, ...LIGHT_TINT_KEYS, ...LIGHT_SOLID_BRAND_KEYS]) {
        root.style.removeProperty(key)
      }
    } else {
      set('--brand-primary', hexColor(colors, 'primary'))
      set('--brand-primary-hover', hexColor(colors, 'primary_hover'))
      set('--brand-primary-active', hexColor(colors, 'primary_active'))
      set('--brand-primary-disabled', hexColor(colors, 'primary_disabled'))
      set('--brand-on-primary', hexColor(colors, 'on_primary'))
      set('--brand-secondary', hexColor(colors, 'secondary'))
      set('--brand-secondary-hover', hexColor(colors, 'secondary_hover'))
      set('--brand-accent', hexColor(colors, 'accent'))
      set('--brand-primary-strong', hexColor(colors, 'primary_strong'))
      set('--brand-primary-soft', hexColor(colors, 'primary_soft'))
      set('--brand-secondary-soft', hexColor(colors, 'secondary_soft'))
      set('--brand-accent-soft', hexColor(colors, 'accent_soft'))
      set('--brand-error-soft', hexColor(colors, 'error_soft'))
      set('--brand-success-soft', hexColor(colors, 'success_soft'))
      set('--brand-warning-soft', hexColor(colors, 'warning_soft'))
      set('--brand-info-soft', hexColor(colors, 'info_soft'))
      set('--brand-background', hexColor(colors, 'background'))
      set('--brand-surface', hexColor(colors, 'surface'))
      set('--brand-surface-strong', hexColor(colors, 'surface_strong'))
      set('--brand-surface-tint', hexColor(colors, 'surface_tint'))
      set('--brand-border', hexColor(colors, 'border'))
      set('--brand-border-strong', hexColor(colors, 'border_strong'))
      set('--brand-border-soft', hexColor(colors, 'border_soft'))
      set('--brand-text-primary', hexColor(colors, 'text_primary'))
      set('--brand-text-secondary', hexColor(colors, 'text_secondary'))
      set('--brand-text-muted', hexColor(colors, 'text_muted'))
      set('--brand-muted', hexColor(colors, 'muted'))
      set('--brand-ring', hexColor(colors, 'ring'))
      set('--brand-scrim', typeof colors?.scrim === 'string' ? colors.scrim : undefined)
    }

    set('--brand-error', hexColor(colors, 'error'))
    set('--brand-success', hexColor(colors, 'success'))
    set('--brand-warning', hexColor(colors, 'warning'))
    set('--brand-info', hexColor(colors, 'info'))

    const radii = typeof radiusPreset === 'string' ? RADIUS_PRESETS[radiusPreset] : undefined
    set('--brand-radius-xs', radii?.xs)
    set('--brand-radius-sm', radii?.sm)
    set('--brand-radius-md', radii?.md)
    set('--brand-radius-lg', radii?.lg)
    set('--brand-radius-xl', radii?.xl)

    set('--brand-font-heading', typographyValue(typography, 'heading_font'))
    set('--brand-font-body', typographyValue(typography, 'body_font'))

    const customKeys: string[] = []
    if (customTokens && typeof customTokens === 'object') {
      for (const [rawKey, rawValue] of Object.entries(customTokens)) {
        if (!/^[a-z][a-z0-9-]{0,63}$/.test(rawKey) || typeof rawValue !== 'string' || rawValue.trim() === '') {
          continue
        }
        const cssVar = `--custom-${rawKey}`
        root.style.setProperty(cssVar, rawValue.trim())
        customKeys.push(cssVar)
      }
    }

    return () => {
      brandKeys.forEach((k) => root.style.removeProperty(k))
      customKeys.forEach((k) => root.style.removeProperty(k))
      root.removeAttribute('data-active-theme')
      root.removeAttribute('data-brand-theme')
    }
  }, [colors, typography, radiusPreset, customTokens, props.brand?.theme_slug, isDark])

  return null
}
